import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { BellOutlined, EyeOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { notificationAPI } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const audienceLabels = {
  all: '全部用户',
  roles: '按角色',
  schools: '指定学校',
  classes: '指定班级',
  accounts: '指定用户',
  contacts: '指定用户'
};

const roleLabels = {
  teacher: '教师',
  parent: '家长',
  school_admin: '学校管理员',
  platform_admin: '平台管理员'
};

const splitTargetInput = (value) => String(value || '')
  .split(/[\s,，；;\n\r]+/)
  .map((item) => item.trim())
  .filter(Boolean);

const renderAudience = (record = {}) => {
  const filters = record.filters || record.payload?.filters || {};
  if (record.audience === 'roles') {
    const roles = (filters.roles || []).map((role) => roleLabels[role] || role);
    return `${audienceLabels.roles}: ${roles.join('、') || '-'}`;
  }
  if (record.audience === 'schools') return `学校ID: ${(filters.schoolIds || []).join('、') || '-'}`;
  if (record.audience === 'classes') return `班级ID: ${(filters.classIds || []).join('、') || '-'}`;
  if (record.audience === 'accounts' || record.audience === 'contacts') {
    const targets = filters.contactKeys || filters.accountInputs || [];
    return `指定用户: ${targets.join('、') || '-'}`;
  }
  return audienceLabels.all;
};

const SystemAnnouncementManager = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [targetType, setTargetType] = useState('all');

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.listAnnouncements();
      if (response.data?.success) {
        setAnnouncements(response.data.data || []);
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      console.error('加载系统公告失败:', error);
      message.error('加载系统公告失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleTargetTypeChange = (value) => {
    setTargetType(value);
    form.setFieldsValue({ roles: [], targetInput: '' });
  };

  const handlePublish = async (values) => {
    try {
      setPublishing(true);
      const payload = {
        title: String(values.title || '').trim(),
        content: String(values.content || '').trim(),
        important: Boolean(values.important),
        popupMode: values.popupMode || 'modal',
        audience: values.targetType || 'all',
        roles: values.roles || [],
        contactKeys: splitTargetInput(values.targetInput),
        targetInput: splitTargetInput(values.targetInput)
      };
      const response = await notificationAPI.publishAnnouncement(payload);
      if (response.data?.success) {
        message.success(`公告已发布，已推送 ${response.data.data?.recipientCount || 0} 人`);
        form.resetFields();
        form.setFieldsValue({
          important: false,
          popupMode: 'modal',
          targetType: 'all',
          roles: [],
          targetInput: ''
        });
        setTargetType('all');
        loadAnnouncements();
        return;
      }
      message.error(response.data?.message || '发布失败');
    } catch (error) {
      console.error('发布系统公告失败:', error);
      message.error(error.response?.data?.message || '发布失败');
    } finally {
      setPublishing(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Space size={8}>
            <Text strong>{text || '系统公告'}</Text>
            {record.important ? <Tag color="red">重要</Tag> : <Tag color="blue">普通</Tag>}
          </Space>
          <Text type="secondary" ellipsis style={{ maxWidth: 220 }}>
            {record.content}
          </Text>
        </Space>
      )
    },
    {
      title: '弹窗',
      dataIndex: 'popupMode',
      key: 'popupMode',
      width: 110,
      render: (value) => <Tag color="geekblue">{value || 'modal'}</Tag>
    },
    {
      title: '发送范围',
      dataIndex: 'audience',
      key: 'audience',
      width: 260,
      render: (_, record) => <Tag color={record.audience === 'all' ? 'green' : 'orange'}>{renderAudience(record)}</Tag>
    },
    {
      title: '送达 / 已读',
      key: 'delivery',
      width: 140,
      render: (_, record) => (
        <Text>
          {record.recipientCount || 0} / {record.readCount || 0}
        </Text>
      )
    },
    {
      title: '发送时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setCurrentItem(record);
            setDetailVisible(true);
          }}
        >
          详情
        </Button>
      )
    }
  ], []);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card
        title={(
          <Space>
            <BellOutlined />
            <span>系统公告</span>
          </Space>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={loadAnnouncements}>
            刷新
          </Button>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="这里发布的公告会写入现有通知链路，小程序登录后或进入系统时会自动弹窗一次。指定用户支持教师码、绑定手机、绑定邮箱、教师邀请码、微信昵称或系统昵称。"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handlePublish}
          initialValues={{
            important: false,
            popupMode: 'modal',
            targetType: 'all',
            roles: [],
            targetInput: ''
          }}
        >
          <Form.Item
            name="title"
            label="公告标题"
            rules={[{ required: true, message: '请输入公告标题' }]}
          >
            <Input maxLength={80} placeholder="例如：系统维护通知" />
          </Form.Item>

          <Form.Item
            name="content"
            label="公告内容"
            rules={[{ required: true, message: '请输入公告内容' }]}
          >
            <TextArea rows={6} maxLength={1000} placeholder="填写要推送给用户的通知内容" />
          </Form.Item>

          <Space size="large" wrap style={{ marginBottom: 8 }}>
            <Form.Item name="important" valuePropName="checked" label="重要公告" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>
            <Form.Item name="popupMode" label="弹窗样式" style={{ marginBottom: 0, minWidth: 180 }}>
              <Select>
                <Option value="modal">标准弹窗</Option>
                <Option value="emphasis">强调弹窗</Option>
              </Select>
            </Form.Item>
            <Form.Item name="targetType" label="发送对象" style={{ marginBottom: 0, minWidth: 180 }}>
              <Select onChange={handleTargetTypeChange}>
                <Option value="all">全部用户</Option>
                <Option value="roles">按角色</Option>
                <Option value="schools">指定学校ID</Option>
                <Option value="classes">指定班级ID</Option>
                <Option value="contacts">指定用户</Option>
              </Select>
            </Form.Item>
          </Space>

          {targetType === 'roles' && (
            <Form.Item name="roles" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
              <Select mode="multiple" placeholder="选择要接收公告的角色">
                <Option value="teacher">教师</Option>
                <Option value="parent">家长</Option>
                <Option value="school_admin">学校管理员</Option>
                <Option value="platform_admin">平台管理员</Option>
              </Select>
            </Form.Item>
          )}

          {['schools', 'classes', 'contacts'].includes(targetType) && (
            <Form.Item
              name="targetInput"
              label={targetType === 'schools' ? '学校ID' : targetType === 'classes' ? '班级ID' : '教师码 / 绑定手机 / 绑定邮箱 / 邀请码 / 昵称'}
              rules={[{ required: true, message: '请输入发送对象' }]}
              extra={targetType === 'contacts'
                ? '支持逗号、空格或换行分隔多个用户。教师码示例：T-ABCD-1234。'
                : '支持逗号、空格或换行分隔多个对象。'}
            >
              <TextArea
                rows={3}
                placeholder={targetType === 'contacts' ? '例如：T-ABCD-1234 或 13800000000 或 teacher@example.com' : '例如：1, 2, 3'}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: 16 }}>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={publishing}>
              发布公告
            </Button>
          </Form.Item>
        </Form>

        <Table
          rowKey="announcementId"
          loading={loading}
          columns={columns}
          dataSource={announcements}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 980 }}
        />
      </Card>

      <Modal
        open={detailVisible}
        title="公告详情"
        onCancel={() => setDetailVisible(false)}
        footer={null}
      >
        {currentItem && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Title level={4} style={{ margin: 0 }}>
              {currentItem.title || '系统公告'}
            </Title>
            <Space wrap>
              <Tag color={currentItem.important ? 'red' : 'blue'}>
                {currentItem.important ? '重要公告' : '普通公告'}
              </Tag>
              <Tag color="green">{renderAudience(currentItem)}</Tag>
              <Tag color="geekblue">{currentItem.popupMode || 'modal'}</Tag>
            </Space>
            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {currentItem.content}
            </Paragraph>
            <Text type="secondary">
              送达 {currentItem.recipientCount || 0} 人，已读 {currentItem.readCount || 0} 人
            </Text>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default SystemAnnouncementManager;
